/**
 * Centralized Token Management Utility
 * Provides secure token refresh, rate limiting, and auto-retry functionality
 * for all API services to use consistently.
 */

import {
  getKeycloakToken,
  isKeycloakAuthenticated,
  refreshKeycloakTokenWithStoredRefreshToken,
  updateKeycloakToken,
} from "../services/keycloak";
import { secureStorage } from "./security";
import { getCurrentRequestLanguage } from "./requestLanguage";
import {
  clearActiveProjectId,
  getRequestScopeHeaders,
} from "./requestScope";

let refreshPromise = null;

const authLog = (...args) => {
  console.debug("TOKEN_MANAGER =>", ...args);
};

const shouldLogoutForRefreshError = (error) => {
  return (
    error?.code === "missing_refresh_token" ||
    error?.code === "refresh_token_expired" ||
    error?.code === "invalid_grant" ||
    error?.status === 400 ||
    error?.status === 401 ||
    error?.status === 403
  );
};

const runSingleFlightRefresh = (refreshFn) => {
  if (!refreshPromise) {
    refreshPromise = refreshFn().finally(() => {
      refreshPromise = null;
    });
  } else {
    authLog("joining in-flight token refresh");
  }

  return refreshPromise;
};

// Secure token refresh function
const secureTokenRefresh = async () => {
  return runSingleFlightRefresh(async () => {
    let newToken = null;

    // First try Keycloak authentication
    if (isKeycloakAuthenticated()) {
      try {
        authLog("refreshing via Keycloak adapter");
        await updateKeycloakToken(() => {
          // Token refreshed successfully
        });
        newToken = getKeycloakToken();
      } catch (keycloakError) {
        authLog("Keycloak adapter refresh failed", keycloakError);
      }
    }

    // Fallback to stored refresh token if Keycloak is not initialized or adapter refresh fails.
    if (!newToken) {
      try {
        newToken = await refreshKeycloakTokenWithStoredRefreshToken();
      } catch (storedRefreshError) {
        authLog("stored refresh token flow failed", storedRefreshError);

        if (shouldLogoutForRefreshError(storedRefreshError)) {
          redirectToLogin(
            "refresh token is missing, expired, invalid, or unauthorized",
          );
          return null;
        }

        throw storedRefreshError;
      }
    }

    if (newToken) {
      secureStorage.setToken(newToken);
      authLog("token refresh succeeded");
      return newToken;
    }

    redirectToLogin("refresh did not return an access token");
    return null;
  }).catch((error) => {
    console.error("TOKEN_MANAGER => secureTokenRefresh error", error);
    throw error;
  });
};

// Redirect to login function
const redirectToLogin = (reason = "authentication failed") => {
  authLog(`redirecting to login: ${reason}`);
  // Clear all tokens
  secureStorage.clearAllTokens();
  clearActiveProjectId();

  // Redirect to login (adjust URL as needed)
  window.location.href = "/login";
};

// Helper function to check if token is expiring soon
const isTokenExpiringSoon = (token, bufferSeconds = 30) => {
  if (!token) return true;

  try {
    // Decode JWT token to get expiration
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));
    const exp = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeUntilExpiry = exp - now;

    // Return true if token expires within buffer time
    return timeUntilExpiry <= bufferSeconds * 1000;
  } catch (error) {
    // If we can't decode the token, assume it's invalid/expiring
    return true;
  }
};

// Create request interceptor for adding tokens and organization headers with just-in-time refresh
export const createRequestInterceptor = () => {
  return async (config) => {
    let token = null;
    config.headers = config.headers || {};

    // Get current token
    if (isKeycloakAuthenticated()) {
      token = getKeycloakToken();
    } else {
      token = secureStorage.getToken();
    }

    // If token is null/undefined, try to refresh
    if (!token) {
      try {
        authLog("no access token found, attempting refresh before request");
        const newToken = await secureTokenRefresh();
        if (newToken) {
          token = newToken;
        } else {
          // If refresh failed, logout
          redirectToLogin();
          return Promise.reject(
            new Error("Authentication failed. You have been logged out."),
          );
        }
      } catch (error) {
        // If refresh failed, logout
        redirectToLogin();
        return Promise.reject(
          new Error("Authentication failed. You have been logged out."),
        );
      }
    }

    // Check if token is expiring soon (within 60 seconds) and refresh if needed
    if (token && isTokenExpiringSoon(token)) {
      try {
        authLog("access token is expiring soon, refreshing before request");
        token = await secureTokenRefresh();
      } catch (error) {
        authLog(
          "pre-request refresh failed; allowing request/response flow to handle it",
          error,
        );
      }
    }

    delete config.headers["X-Organization-ID"];
    delete config.headers["x-organization-id"];
    delete config.headers["X-Project-ID"];
    delete config.headers["x-project-id"];
    Object.assign(
      config.headers,
      getRequestScopeHeaders(config.requestScope),
    );

    // Add token if available
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  };
};

// Create response interceptor for handling 401s
export const createResponseInterceptor = (axiosInstance) => {
  return {
    onFulfilled: (response) => response,
    onRejected: async (error) => {
      const originalRequest = error.config;

      // If the error is 401 and we haven't tried to refresh the token yet
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        try {
          // Use secure token refresh with rate limiting
          authLog("401 received, attempting refresh before retry");
          const newToken = await secureTokenRefresh();

          if (newToken) {
            // Update the original request with the new token
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;

            // Retry the original request ONLY ONCE
            return axiosInstance(originalRequest);
          } else {
            // Token refresh failed and user was logged out
            return Promise.reject(
              new Error("Authentication failed. You have been logged out."),
            );
          }
        } catch (refreshError) {
          if (shouldLogoutForRefreshError(refreshError)) {
            redirectToLogin("refresh failed after 401");
          }

          return Promise.reject(
            new Error("Authentication failed. You have been logged out."),
          );
        }
      }

      return Promise.reject(error);
    },
  };
};

// Setup function to apply both interceptors to an axios instance
export const setupSecureInterceptors = (axiosInstance) => {
  // Add request interceptor
  axiosInstance.interceptors.request.use(
    (config) => {
      // Language header
      config.headers = config.headers || {};
      config.headers["Accept-Language"] = getCurrentRequestLanguage();

      const modifiedConfig = createRequestInterceptor()(config);

      return modifiedConfig;
    },
    (error) => Promise.reject(error),
  );

  // Add response interceptor
  const responseInterceptor = createResponseInterceptor(axiosInstance);
  axiosInstance.interceptors.response.use(
    responseInterceptor.onFulfilled,
    responseInterceptor.onRejected,
  );

  return axiosInstance;
};

// Token management API
export const tokenManager = {
  // Manual token refresh
  refreshTokenNow: async () => {
    authLog("refreshTokenNow");
    const newToken = await secureTokenRefresh();
    if (!newToken) {
      // User was already logged out by secureTokenRefresh
      redirectToLogin("manual refresh returned no access token");
      return; // Exit early - don't return null
    }
    return newToken;
  },

  refreshIfExpired: async () => {
    authLog("refreshIfExpired");
    const token = secureStorage.getToken();
    if (isTokenExpiringSoon(token)) {
      await tokenManager.refreshTokenNow();
    }
  },

  // Force logout and redirect
  forceLogout: () => redirectToLogin(),

  // Setup interceptors on any axios instance
  setupInterceptors: setupSecureInterceptors,

  // Direct token access (for compatibility)
  getToken: () => secureStorage.getToken(),
  getRefreshToken: () => secureStorage.getRefreshToken(),
  isAuthenticated: () => isKeycloakAuthenticated(),
};

// Token refresh is now handled just-in-time before each request
// No need for intervals or cleanup listeners

export default tokenManager;

window.tokenManager = tokenManager;
